// app-ui/src/components/project/ProjectList.tsx
import React, { useState, useEffect } from 'react';
import { Project } from '../types';
import apiClient, { TokenManager } from '../../../../shared/lib/apiClient';
import Modal from '../../../../shared/ui/Modal';
import { initializeWebSocketConnection } from '../../../../shared/lib/comm/gateway_ws/connection';
import { useWebSocketStatus } from '../../../../shared/lib/comm/gateway_ws/useWebSocketStatus';
import { getRouter } from '../../../../shared/lib/comm/gateway_ws/connection';

export type ProjectAccessPermission = 'owner' | 'read' | 'readwrite';

interface ProjectListProps {
    onProjectSelect: (projectId: number | string, projectName?: string, permission?: ProjectAccessPermission) => void;
    refreshTrigger: number;
    selectedProjectId: number | string | null;
    triggerRefresh: () => void;
    /** Opens project-level Git settings; shown as an icon on each project card when user can edit. */
    onGitSettingsClick?: (project: Project) => void;
}

const ProjectList: React.FC<ProjectListProps> = ({
    onProjectSelect,
    refreshTrigger,
    selectedProjectId,
    triggerRefresh,
    onGitSettingsClick,
}) => {
    const [projects, setProjects] = useState<Project[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [listError, setListError] = useState<string | null>(null);
    const [deletingProjectId, setDeletingProjectId] = useState<number | string | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
    const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
    
    // Use WebSocket status hook for health monitoring
    const wsStatus = useWebSocketStatus();

    useEffect(() => {
        const fetchProjects = async () => {
            setLoading(true);
            setListError(null);
            try {
                // Get user ID from localStorage
                const userId = TokenManager.getUserId();
                if (!userId) {
                    setListError('User ID not found. Please sign in again.');
                    setLoading(false);
                    return;
                }
                
                // Ensure WebSocket is initialized (only if not already initialized)
                // WebSocket is already initialized globally, so we just check status here
                try {
                    // Log WebSocket status from hook
                    console.log(`🔌 [ProjectList] WebSocket state: ${wsStatus.state}`);
                    
                    // If not connected, initialize connection
                    if (!wsStatus.isConnected) {
                        console.log(`🔌 [ProjectList] WebSocket is ${wsStatus.state}, initializing connection...`);
                        initializeWebSocketConnection();
                    }
                    
                    // If connected, check if health check has been sent
                    if (wsStatus.isConnected) {
                        const router = getRouter();
                        const healthStatus = router.getHealthCheck().getHealthStatus();
                        const hasReceivedPong = healthStatus.lastPongTimestamp !== null;
                        
                        if (!hasReceivedPong) {
                            console.log('🔌 [ProjectList] WebSocket connected but no pong received yet, health check should be running...');
                        } else {
                            const timeSincePong = healthStatus.lastPongTimestamp ? Date.now() - healthStatus.lastPongTimestamp : null;
                            console.log(`🔌 [ProjectList] WebSocket connected and health check active (last pong: ${timeSincePong ? Math.round(timeSincePong / 1000) : 'N/A'}s ago)`);
                        }
                    }
                } catch (error) {
                    console.warn('🔌 [ProjectList] Error checking WebSocket status:', error);
                    // Continue with API call even if WebSocket check fails
                }
                
                // Fetch projects with user_id query parameter
                const data = await apiClient<Project[]>(`/projects?user_id=${encodeURIComponent(userId)}`, { method: 'GET' });
                setProjects(data);
            } catch (err: any) {
                setListError(err.message || 'Failed to fetch projects');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchProjects();
    }, [refreshTrigger, wsStatus.state, wsStatus.isConnected]);

    const handleDeleteClick = (project: Project, e: React.MouseEvent) => {
        e.stopPropagation();
        setProjectToDelete(project);
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = async () => {
        if (!projectToDelete) return;
        
        setShowDeleteConfirm(false);
        setDeletingProjectId(projectToDelete.id);
        
        try {
            const uid = TokenManager.getUserId();
            if (!uid) {
                alert('You must be signed in to delete a project.');
                return;
            }
            const params = new URLSearchParams({ user_id: uid });
            await apiClient(`/projects/${projectToDelete.id}?${params.toString()}`, {
                method: 'DELETE',
            });
            triggerRefresh();
        } catch (err: any) {
            console.error('Failed to delete project:', err);
            alert('Failed to delete project. Please try again.');
        } finally {
            setDeletingProjectId(null);
            setProjectToDelete(null);
        }
    };

    const handleDeleteCancel = () => {
        setShowDeleteConfirm(false);
        setProjectToDelete(null);
    };

    if (loading) {
        return (
            <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                    <div key={i} className="animate-pulse">
                        <div className="h-20 bg-white/10 rounded-xl"></div>
                    </div>
                ))}
            </div>
        );
    }

    if (listError) {
        // Don't show error state, just return empty state
        return null;
    }

    const hasSelection = selectedProjectId !== null;
    const selectionInList =
        hasSelection && projects.some((p) => String(p.id) === String(selectedProjectId));
    const compactOtherProjects = hasSelection && selectionInList;

    const renderProjectFull = (project: Project) => {
        const isDeleting = deletingProjectId === project.id;
        const isSelected = String(selectedProjectId) === String(project.id);
        const canEditGit =
            project.permission === 'owner' || project.permission === 'readwrite';
        const showDelete =
            project.permission !== 'read' && project.permission !== 'readwrite';
        const showGitIcon = Boolean(onGitSettingsClick && canEditGit);
        const showActionIcons = showGitIcon || showDelete;

        return (
            <div
                key={project.id}
                className={`
                                    group relative px-3 pb-3 pt-1 rounded-xl border transition-all duration-300 cursor-pointer transform hover:scale-[1.02]
                                    ${isSelected 
                                        ? 'bg-gradient-to-r from-purple-500/20 to-blue-500/20 border-purple-400/30 shadow-lg shadow-purple-500/10' 
                                        : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20 hover:shadow-lg'
                                    }
                                    ${isDeleting ? 'opacity-50' : ''}
                                `}
                onClick={() => !isDeleting && onProjectSelect(project.id, project.name, project.permission)}
            >
                <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                        <h3
                            className={`
                                            text-sm sm:text-base font-semibold truncate leading-tight
                                            ${isSelected 
                                                ? 'text-white' 
                                                : 'text-white'
                                            }
                                        `}
                        >
                            {project.name}
                        </h3>
                        <p
                            className={`
                                            text-xs sm:text-sm mt-2
                                            ${isSelected 
                                                ? 'text-purple-200' 
                                                : 'text-gray-300'
                                            }
                                        `}
                        >
                            Created: {new Date(project.created_at).toLocaleDateString()}
                        </p>
                        {project.permission && project.permission !== 'owner' && (
                            <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded bg-blue-500/20 text-blue-200 border border-blue-400/30">
                                Shared with you {project.permission === 'readwrite' ? '(Read & Write)' : '(Read)'}
                            </span>
                        )}
                        {/* Project ID for debugging */}
                        <span className="text-xs text-gray-400 mt-1 block">ID: {project.id}</span>
                    </div>

                    {showActionIcons && (
                        <div className="flex items-center gap-0.5 shrink-0">
                            {showGitIcon && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onGitSettingsClick?.(project);
                                    }}
                                    className="p-1.5 rounded-lg border border-green-400/30 text-green-300 bg-gradient-to-r from-green-500/15 to-emerald-500/15 hover:from-green-500/25 hover:to-emerald-500/25 hover:border-green-400/50 transition-all duration-200"
                                    title="Git settings"
                                    aria-label="Git settings"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                                        />
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                        />
                                    </svg>
                                </button>
                            )}
                            {showDelete && (
                                <button
                                    type="button"
                                    onClick={(e) => handleDeleteClick(project, e)}
                                    disabled={isDeleting}
                                    className={`
                                                        p-2 rounded-lg transition-all duration-200
                                                        ${isDeleting 
                                                            ? 'text-gray-400 cursor-not-allowed' 
                                                            : 'text-red-400 hover:text-red-300 hover:bg-red-500/10'
                                                        }
                                                    `}
                                    title="Delete project"
                                >
                                    {isDeleting ? (
                                        <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                            />
                                        </svg>
                                    ) : (
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                            />
                                        </svg>
                                    )}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderProjectCompact = (project: Project) => {
        const isDeleting = deletingProjectId === project.id;
        const canEditGit =
            project.permission === 'owner' || project.permission === 'readwrite';
        const showDelete =
            project.permission !== 'read' && project.permission !== 'readwrite';
        const showGitIcon = Boolean(onGitSettingsClick && canEditGit);
        const showActionIcons = showGitIcon || showDelete;

        return (
            <div
                key={project.id}
                className={`
                    group flex cursor-pointer items-center gap-1 rounded-lg border border-white/10 bg-white/[0.06] px-2 py-1.5 transition-colors
                    hover:border-white/20 hover:bg-white/[0.11]
                    ${isDeleting ? 'pointer-events-none opacity-50' : ''}
                `}
                onClick={() => !isDeleting && onProjectSelect(project.id, project.name, project.permission)}
            >
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-white/95">{project.name}</span>
                {showActionIcons && (
                    <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                        {showGitIcon && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onGitSettingsClick?.(project);
                                }}
                                className="rounded p-1 text-green-300/90 hover:bg-green-500/15"
                                title="Git settings"
                                aria-label="Git settings"
                            >
                                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                                    />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                            </button>
                        )}
                        {showDelete && (
                            <button
                                type="button"
                                onClick={(e) => handleDeleteClick(project, e)}
                                disabled={isDeleting}
                                className="rounded p-1 text-red-400/90 hover:bg-red-500/15"
                                title="Delete project"
                                aria-label="Delete project"
                            >
                                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                </svg>
                            </button>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <h2 className="text-lg sm:text-xl font-semibold text-white">
                        Projects
                    </h2>
                </div>
                
                <span className="text-xs sm:text-sm text-gray-300 bg-white/10 px-3 py-1 rounded-full border border-white/20">
                    {projects.length}
                </span>
            </div>

            {projects.length === 0 ? (
                <div className="text-center py-12">
                    <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-full flex items-center justify-center border border-white/10">
                        <svg className="w-10 h-10 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-medium text-white mb-3">
                        No projects yet
                    </h3>
                    <p className="text-gray-300 text-sm leading-relaxed">
                        Create your first project to begin your creative journey.
                    </p>
                </div>
            ) : (
                <div className="space-y-3">
                    {compactOtherProjects ? (
                        <>
                            <div className="mb-2 flex flex-col gap-1 border-b border-white/10 pb-2">
                                {projects
                                    .filter((p) => String(p.id) !== String(selectedProjectId))
                                    .map((p) => renderProjectCompact(p))}
                            </div>
                            {projects
                                .filter((p) => String(p.id) === String(selectedProjectId))
                                .map((p) => renderProjectFull(p))}
                        </>
                    ) : (
                        projects.map((p) => renderProjectFull(p))
                    )}
                </div>
            )}

            {showDeleteConfirm && projectToDelete && (
                <Modal
                    isOpen={showDeleteConfirm}
                    onClose={handleDeleteCancel}
                    title="Confirm Deletion"
                    size="sm"
                >
                    <div className="text-center">
                        <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-red-500/20 to-red-600/20 rounded-full flex items-center justify-center border border-red-400/30">
                            <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                        </div>
                        
                        <h3 className="text-lg font-semibold text-white mb-3">
                            Delete Project
                        </h3>
                        
                        <p className="text-gray-300 text-sm leading-relaxed mb-6">
                            Are you sure you want to delete <span className="text-white font-medium">"{projectToDelete.name}"</span>? 
                            This action cannot be undone.
                        </p>
                        
                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={handleDeleteCancel}
                                className="px-6 py-2 rounded-lg border border-white/20 text-white hover:bg-white/10 transition-all duration-200"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteConfirm}
                                className="px-6 py-2 rounded-lg bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-lg"
                            >
                                Delete Project
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default ProjectList; 